import ApiUtil from '../utils/apiUtil'
import { getAgentFromStorage } from '../utils/appUtils'

type ServiceResponse = any

/**
 * Service for managing roleplay media (images and videos)
 * All endpoints use the quiz/roleplay prefix
 */
export default class RoleplayMediaService {
  /**
   * Get agent ID and validate it exists
   * @throws Error if agent_id is not found
   * @returns agent_id string
   */
  private getValidatedAgentId(): string {
    const agentId = getAgentFromStorage()
    if (!agentId || agentId.trim() === '') {
      throw new Error(
        'Agent ID is required but not found. Please select an agent.',
      )
    }
    return agentId
  }

  /**
   * Upload image for roleplay
   * @param formData - FormData with 'file' and optional 'filepath'
   * @returns Promise with response containing media info
   * @throws Error if agent_id is missing
   */
  async uploadImage(formData: FormData): Promise<ServiceResponse> {
    const agentId = this.getValidatedAgentId()
    const res = await ApiUtil.SendAsync<any>({
      url: `quiz/roleplay/images/upload?agent_id=${agentId}`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res
  }

  /**
   * Upload video for roleplay
   * @param formData - FormData with 'file' and optional 'filepath'
   * @returns Promise with response containing media info
   * @throws Error if agent_id is missing
   */
  async uploadVideo(formData: FormData): Promise<ServiceResponse> {
    const agentId = this.getValidatedAgentId()
    const res = await ApiUtil.SendAsync<any>({
      url: `quiz/roleplay/videos/upload?agent_id=${agentId}`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res
  }

  /**
   * Get media info by ID (image or video)
   * @param mediaId - The media ID to retrieve
   * @returns Promise with response containing: { url, _id, file_name, file_size, mime_type }
   * @throws Error if agent_id is missing
   */
  async getMediaInfo(mediaId: string): Promise<ServiceResponse> {
    const agentId = this.getValidatedAgentId()
    const res = await ApiUtil.SendAsync<any>({
      url: `quiz/roleplay/media/${mediaId}?agent_id=${agentId}`,
      method: 'GET',
    })
    return res
  }

  /**
   * Update media file by ID (replace existing image/video)
   * @param mediaId - The media ID to update
   * @param formData - FormData with 'file' and optional 'filepath'
   * @returns Promise with response containing updated media info
   * @throws Error if agent_id is missing
   */
  async updateMedia(
    mediaId: string,
    formData: FormData,
  ): Promise<ServiceResponse> {
    const agentId = this.getValidatedAgentId()
    const res = await ApiUtil.SendAsync<any>({
      url: `quiz/roleplay/media/${mediaId}?agent_id=${agentId}`,
      method: 'PUT',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res
  }

  /**
   * Get stream URL for media (for direct access)
   * @param mediaId - The media ID
   * @returns Full stream URL string
   * @throws Error if agent_id is missing
   */
  getStreamUrl(mediaId: string): string {
    const agentId = this.getValidatedAgentId()
    return `${(window as any).APP_API_URL}/quiz/roleplay/media/${mediaId}/stream?agent_id=${agentId}`
  }
}
